import React, { useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  FiChevronLeft,
  FiCheck,
  FiCheckCircle,
  FiKey,
  FiShield,
  FiEye,
  FiEyeOff,
} from 'react-icons/fi';
import { authApi } from '../../api/auth';
import PageLayout from '../../components/layout/PageLayout';
import { AccountBoundary, useSettings, useRequestScope } from '../settings/shared';
import DraftGuard from '../contact/ContactDraftGuard';
import { useExpandedText } from '../notifications/useExpandedText';
import { translate, validateForm, requestError } from './model';
import baseline from '../../app-ui/baseline';
import './Password.css';
export function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  error,
  required = false,
  secret = false,
  toggle = false,
  autoComplete,
  t,
}) {
  const [visible, setVisible] = useState(false),
    ref = useRef();
  return (
    <div className="pp-password-field">
      <label htmlFor={id}>
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      <div className="pp-password-input">
        <input
          ref={ref}
          id={id}
          name={id}
          type={secret && !visible ? 'password' : id.endsWith('email') ? 'email' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          autoCapitalize="none"
          spellCheck={false}
          placeholder={placeholder}
          required={required}
          aria-invalid={!!error}
          aria-describedby={error ? id + '-error' : undefined}
        />
        {toggle && (
          <button
            type="button"
            aria-label={t(visible ? 'auth.hideField' : 'auth.showField', { label })}
            onClick={() => {
              setVisible((v) => !v);
              ref.current?.focus();
            }}
          >
            {visible ? <FiEyeOff size={19} /> : <FiEye size={19} />}
          </button>
        )}
      </div>
      {error && (
        <small role="alert" id={id + '-error'}>
          {error}
        </small>
      )}
    </div>
  );
}
export default function PasswordPage({ mode }) {
  const loc = useLocation();
  return (
    <AccountBoundary
      key={loc.key}
      component={mode === 'change' ? Change : mode === 'forgot' ? Forgot : Reset}
    />
  );
}
function Change({ user, validateSession }) {
  return user ? (
    <Form mode="change" user={user} validateSession={validateSession} />
  ) : (
    <Navigate to="/login?redirectUrl=%2Fpassword-change" replace />
  );
}
function Forgot({ user, validateSession }) {
  return <Form mode="forgot" user={user} validateSession={validateSession} />;
}
function Reset({ user, validateSession }) {
  return <Form mode="reset" user={user} validateSession={validateSession} />;
}
function Form({ mode, user, validateSession }) {
  const { style, locale, dark } = useSettings(),
    { search } = useLocation(),
    nav = useNavigate(),
    request = useRequestScope(user),
    expanded = useExpandedText(),
    t = (k, v) => translate(locale, k, v);
  const [initialToken] = useState(() => new URLSearchParams(search).get('token') || '');
  const [form, setForm] = useState({
      current: '',
      password: '',
      confirm: '',
      email: '',
      token: initialToken,
    }),
    [errors, setErrors] = useState({}),
    [busy, setBusy] = useState(false),
    [complete, setComplete] = useState(false);
  const lock = useRef(false),
    guard = useRef({ released: false });
  const social = mode === 'change' && Boolean(user?.provider);
  guard.current = {
    released: complete,
    dirty:
      !complete &&
      !social &&
      mode !== 'forgot' &&
      Boolean(form.current || form.password || form.confirm || form.token !== initialToken),
    busy,
  };
  const edit = (k, value) => {
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((e) => ({ ...e, [k]: '', submit: '' }));
  };
  const title = t(
    mode === 'change'
      ? 'auth.changePassword'
      : mode === 'forgot'
        ? 'auth.findPassword'
        : 'auth.resetPassword',
  );
  const back = () =>
    window.history.state?.idx > 0
      ? nav(-1)
      : nav(mode === 'change' ? '/mypage/settings' : '/login');
  const submit = async (event) => {
    event.preventDefault();
    if (lock.current || social || !validateSession()) return;
    const e = validateForm(mode, form, t);
    setErrors(e);
    if (Object.keys(e).length) return;
    lock.current = true;
    setBusy(true);
    const r = request();
    try {
      if (mode === 'change') await authApi.changePassword(form.current, form.password, r.options);
      else if (mode === 'forgot')
        await authApi.sendPasswordResetEmail(form.email.trim(), r.options);
      else await authApi.resetPassword(form.token.trim(), form.password, r.options);
      if (r.current()) {
        setComplete(true);
        setForm({ current: '', password: '', confirm: '', email: '', token: '' });
      }
    } catch (err) {
      if (r.current())
        setErrors({
          submit: requestError(
            err,
            t(
              mode === 'change'
                ? 'auth.changeFailed'
                : mode === 'forgot'
                  ? 'auth.resetEmailFailed'
                  : 'auth.resetFailed',
            ),
          ),
        });
    } finally {
      lock.current = false;
      if (r.mounted()) {
        setBusy(false);
        if (!r.current()) setErrors({ submit: t('common.retryLater') });
      }
      r.finish();
    }
  };
  const field = (
    key,
    label,
    placeholder,
    { required = mode !== 'change', secret = false, autoComplete } = {},
  ) => (
    <Field
      key={key}
      id={'password-' + key}
      label={t(label)}
      placeholder={placeholder ? t(placeholder) : undefined}
      value={form[key]}
      error={errors[key]}
      required={required}
      secret={secret}
      toggle={mode === 'change' && secret}
      autoComplete={autoComplete}
      t={t}
      onChange={(value) => edit(key, value)}
    />
  );
  return (
    <PageLayout paddingTop={0}>
      <div
        className={'pp-settings pp-password pp-password-' + mode + (expanded ? ' is-expanded' : '')}
        style={{
          ...style,
          '--password-surface': baseline.tokens[dark ? 'darkColors' : 'lightColors'].surface,
        }}
      >
        <DraftGuard state={guard} t={t} />
        <header className={mode === 'reset' ? 'pp-password-reset-header' : 'pp-settings-header'}>
          {mode !== 'reset' && (
            <button type="button" aria-label={t('common.back')} disabled={busy} onClick={back}>
              <FiChevronLeft size={22} />
            </button>
          )}
          <h1>{title}</h1>
        </header>
        {complete || social ? (
          <section
            className={mode === 'change' ? 'pp-password-success' : 'pp-password-result'}
            role="status"
          >
            {mode === 'change' && (
              <span className={'pp-password-success-icon' + (social ? ' social' : '')}>
                {social ? <FiShield size={34} /> : <FiCheckCircle size={34} />}
              </span>
            )}
            <div>
              <h2>
                {t(
                  social
                    ? 'auth.socialAccount'
                    : mode === 'change'
                      ? 'auth.passwordChangedCasual'
                      : mode === 'forgot'
                        ? 'auth.emailSent'
                        : 'auth.passwordChanged',
                )}
              </h2>
              {(social || mode !== 'reset') && (
                <p>
                  {t(
                    social
                      ? 'auth.manageAtProvider'
                      : mode === 'forgot'
                        ? 'auth.openResetLink'
                        : 'auth.useNewPassword',
                  )}
                </p>
              )}
            </div>
            {mode !== 'forgot' && (
              <button
                className={'pp-password-submit' + (social ? ' secondary' : '')}
                onClick={() => (mode === 'reset' ? nav('/login', { replace: true }) : back())}
              >
                {t(mode === 'reset' ? 'auth.login' : 'auth.backToSettings')}
              </button>
            )}
          </section>
        ) : (
          <>
            {mode === 'change' && (
              <section className="pp-password-guide">
                <span className="pp-password-guide-icon">
                  <FiKey size={20} />
                </span>
                <div>
                  <strong>{t('auth.securePassword')}</strong>
                  <p>
                    <FiCheck size={14} />
                    {t('auth.ruleCombination')}
                  </p>
                  <p>
                    <FiCheck size={14} />
                    {t('auth.ruleLength')}
                  </p>
                </div>
              </section>
            )}
            <form onSubmit={submit} noValidate>
              <fieldset disabled={busy}>
                {mode === 'forgot' ? (
                  <>
                    <p className="pp-password-description">{t('auth.enterRegisteredEmail')}</p>
                    {field('email', 'auth.email', 'auth.emailPlaceholder', {
                      required: true,
                      autoComplete: 'email',
                    })}
                  </>
                ) : (
                  <>
                    {mode === 'change' &&
                      field('current', 'auth.currentPassword', null, {
                        secret: true,
                        autoComplete: 'current-password',
                      })}
                    {mode === 'reset' &&
                      !initialToken &&
                      field('token', 'auth.resetToken', 'auth.resetTokenPlaceholder', {
                        required: true,
                        autoComplete: 'off',
                      })}
                    {field('password', 'auth.newPassword', 'auth.passwordRuleShort', {
                      secret: true,
                      required: mode === 'reset',
                      autoComplete: 'new-password',
                    })}
                    {field(
                      'confirm',
                      'auth.newPasswordConfirm',
                      mode === 'reset' ? 'auth.passwordConfirmPlaceholder' : null,
                      { secret: true, required: mode === 'reset', autoComplete: 'new-password' },
                    )}
                  </>
                )}
                {errors.submit && (
                  <p className="pp-password-error" role="alert">
                    {errors.submit}
                  </p>
                )}
                <button className="pp-password-submit" type="submit" aria-busy={busy}>
                  {t(
                    busy
                      ? mode === 'forgot'
                        ? 'auth.sending'
                        : mode === 'reset'
                          ? 'auth.changing'
                          : 'common.loading'
                      : mode === 'forgot'
                        ? 'auth.getResetLink'
                        : mode === 'reset'
                          ? 'auth.resetPassword'
                          : 'auth.changePassword',
                  )}
                </button>
              </fieldset>
            </form>
          </>
        )}
      </div>
    </PageLayout>
  );
}
